import { v4 as uuidv4 } from 'uuid';

import { convertKnmOkvedsFormValueToKnmData } from 'KnmPackage/MainModule/common/convertKnmOkvedsFormValueToKnmData';
import { IKnmSubject248 } from 'APIPackage/KnmModule/interfaces/IKnmSubject248';

import { IKnmFormSubject } from '../interfaces/IKnmFormSubject';
import { IKnmErknmFormSubject } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { IErvkObjectsOfControlSubjectData } from '../../../APIPackage/KnmModule/interfaces/IErvkObjectsOfControlSubjectData';
import { IKnmDistrictSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { KnmSubjectTypesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { mapSubjectTypeCodeToName } from '../../KnmObjectsModule/common/mapSubjectTypeCodeToName';
import { ITargetEntity } from '../../../APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IDecisionOrganizationForm } from '../../../DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';
import { ICountrySelectOption } from '../../../CorePackage/CatalogsModule/interfaces/ICountrySelectOption';

const composeOrganizationName = (source: IErvkObjectsOfControlSubjectData): string | undefined => {
  switch (source.subjectType) {
    case 'IP':
    case 'FL':
    case 'IFL': {
      const fio = [source.surName, source.firstName, source.middleName].filter(value => !!value).join(' ');

      if (fio) {
        return source.subjectType === 'IP' ? `Индивидуальный предприниматель "${fio}"` : fio;
      }

      return undefined;
    }

    default:
      return source.organizationFullName || source.organizationShortName;
  }
};

export const convertErvkSubjectToKnmOrganization = (
  source: IErvkObjectsOfControlSubjectData,
  subjectRegion: IKnmDistrictSelectOption | undefined,
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined,
  countriesSelectOptions: ICountrySelectOption[] | undefined,
): IKnmErknmFormSubject => {
  return {
    inspectionResults: undefined,
    listId: '',
    inn: source.inn,
    ogrn: source.ogrn,
    position: undefined,
    okveds: undefined,
    addressFns: source.address,
    organizationName: composeOrganizationName(source),
    firstOrganizationName: undefined,
    mspCode: undefined,
    checkSubjectResult: null,
    dateCreate: undefined,
    isAutocomplete: undefined,
    isFiz: source.subjectType === KnmSubjectTypesEnum.FIZ,
    guid: uuidv4(),
    isDocumentOrganization: false,
    nkoForeignAgentsRegistryNumber: undefined,
    nkoForeignAgentsRegistryInclusionDate: undefined,
    nkoForeignAgentsRegistryExclusionDate: undefined,
    isNko: undefined,
    subjectType: subjectTypeOptions?.find(x => x.label === mapSubjectTypeCodeToName(source.subjectType)),
    rafpNza: source.rafp,
    smevRequestStatus: undefined,
    kpp: source.kpp,
    isForeignSubjectAgencyNotRegistredInRf: undefined,
    country: source?.nationality ? countriesSelectOptions?.find(c => c.title === source.nationality) : undefined,
    isSonko: undefined,
    locationAddress: source.address,
    workAddress: undefined,
    factualAddress: undefined,
    snils: source.snils,
    document: source.identityDocumentNumber,
    epguNotifications: null,
    ervkId: source.ervkId ?? undefined,
    isPresentInItRegistry: undefined,
    itAccreditationResponse: undefined,
    smevItAccreditationRequestStatus: undefined,
    locationregion: subjectRegion,
    locationnotInFIAS: !source.locationIsFromFias,
  };
};

export const convertErvkSubjectToPmOrganization = (
  source: IErvkObjectsOfControlSubjectData,
  subjectRegion: IKnmDistrictSelectOption | undefined,
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined,
  countriesSelectOptions: ICountrySelectOption[] | undefined,
): IKnmFormSubject => {
  return {
    inn: source.inn,
    ogrn: source.ogrn,
    organizationName: composeOrganizationName(source),
    isFiz: source.subjectType === KnmSubjectTypesEnum.FIZ,
    subjectTypePm: subjectTypeOptions?.find(x => x.label === mapSubjectTypeCodeToName(source.subjectType)),
    snils: source.snils,
    locationAddress: source.address,
    document: source.identityDocumentNumber,
    country: source?.nationality ? countriesSelectOptions?.find(c => c.title === source.nationality) : undefined,
    rafpNza: source.rafp,
    kpp: source.kpp,
    ervkId: source.ervkId !== null ? source.ervkId : undefined,
    organizationGuid: uuidv4(),
    isFilledFromLink: false,
    locationfiasRegion: subjectRegion,
    locationregion: subjectRegion,
    locationnotInFIAS: !source.locationIsFromFias,
  };
};

export const convertErvkSubjectToDecisionOrganization = (
  source: IErvkObjectsOfControlSubjectData,
  subjectRegion: IKnmDistrictSelectOption | undefined,
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined,
  countriesSelectOptions: ICountrySelectOption[] | undefined,
): IDecisionOrganizationForm => {
  return {
    guid: uuidv4(),
    inn: source.inn,
    ogrn: source.ogrn,
    organizationType: subjectTypeOptions?.find(x => x.label === mapSubjectTypeCodeToName(source.subjectType)),
    originalName: composeOrganizationName(source),
    originalNameReactQuillEditor: undefined,
    name: composeOrganizationName(source),
    mspCode: undefined,
    checkSubjectResult: null,
    isAutocomplete: undefined,
    isNew: true,
    nkoForeignAgentsRegistryNumber: undefined,
    nkoForeignAgentsRegistryInclusionDate: undefined,
    nkoForeignAgentsRegistryExclusionDate: undefined,
    isNko: undefined,
    kpp: source.kpp,
    smevRequestStatus: undefined,
    rafpNza: source.rafp,
    isForeignSubjectAgencyNotRegisteredInRf: undefined,
    country: source?.nationality ? countriesSelectOptions?.find(c => c.title === source.nationality) : undefined,
    isSonko: undefined,
    locationAddress: source.address,
    workAddress: undefined,
    factualAddress: undefined,
    snils: source.snils,
    document: source.identityDocumentNumber,
    epguNotifications: undefined,
    ervkId: source.ervkId || undefined,
    isPresentInItRegistry: undefined,
    itAccreditationResponse: undefined,
    smevItAccreditationRequestStatus: undefined,
    locationregion: subjectRegion,
    locationnotInFIAS: !source.locationIsFromFias,
    locationfiasRegion: subjectRegion,
    locationRegionCodeFromFns: undefined,
  };
};

export const convertSubjectFormValuesToKnmData = (
  values?: IKnmFormSubject,
  guid?: string | null,
): IKnmSubject248 | null => {
  if (!values) {
    return null;
  }

  const {
    inn,
    ogrn,
    okveds,
    organizationName,
    organizationAutocomplete: isAutocomplete,
    isFiz,
    mspCode,
    isSonko,
    subjectTypePm,
    subjectTypeKnm,
    country,
    document,
    kpp,
    rafpNza,
    workAddress,
    factualAddress,
    snils,
    smevRequestStatus,
    nkoForeignAgentsRegistryNumber,
    nkoForeignAgentsRegistryInclusionDate,
    nkoForeignAgentsRegistryExclusionDate,
    ervkId,
    firstOrganizationName,
    dateCreate,
    checkSubjectResult,
    itAccreditationResponse,
    isPresentInItRegistry,
    smevItAccreditationRequestStatus,
    position,
    inspectionResults,
    isFilledFromLink,
  } = values;

  const getAddressFields = () => {
    const isNotInFIAS = values[`locationnotInFIAS` as keyof typeof values] as boolean | undefined;
    const addressField = `locationAddress` as keyof typeof values;
    const address = values[addressField] as string | undefined;
    const region = values[`locationregion` as keyof typeof values] as
      | { id: number | string; label?: string }
      | undefined;
    const manualAddress = values[`locationmanualAddress` as keyof typeof values] as string | undefined;
    const fiasRegion = values[`locationfiasRegion` as keyof typeof values] as
      | { id: number | string; label?: string }
      | undefined;

    if (isNotInFIAS === true) {
      const regionId = region && 'id' in region && region.id != null ? String(region.id) : null;

      return {
        address: address || null,
        region: regionId,
        manualAddress: manualAddress || null,
        notInFIAS: true,
        fiasRegion: null,
      };
    }
    const fiasRegionId = fiasRegion && 'id' in fiasRegion && fiasRegion.id != null ? String(fiasRegion.id) : null;

    return {
      address: address || null,
      region: null,
      manualAddress: null,
      notInFIAS: isNotInFIAS === false ? false : null,
      fiasRegion: fiasRegionId,
      locationRegionCodeFromFns: values?.locationRegionCodeFromFns,
    };
  };

  const locationFields = getAddressFields();

  return {
    inn: inn || null,
    ogrn: ogrn || null,
    okveds: convertKnmOkvedsFormValueToKnmData(okveds),
    addressFns: null,
    organizationName: organizationName || null,
    mspCode: mspCode || null,
    checkSubjectResult: checkSubjectResult || null,
    dateCreate: dateCreate || null,
    firstOrganizationName: firstOrganizationName || null,
    guid: guid || null,
    act: null,
    isFiz: typeof isFiz === 'boolean' ? isFiz : null,
    resultDecisions: null,
    isAutocomplete: typeof isAutocomplete === 'boolean' ? isAutocomplete : null,
    isDocumentOrganization: null,
    nkoForeignAgentsRegistryNumber: nkoForeignAgentsRegistryNumber || null,
    nkoForeignAgentsRegistryInclusionDate: nkoForeignAgentsRegistryInclusionDate || null,
    nkoForeignAgentsRegistryExclusionDate: nkoForeignAgentsRegistryExclusionDate || null,
    isNko: !!nkoForeignAgentsRegistryInclusionDate,
    subjectType: subjectTypePm || subjectTypeKnm || null,
    kpp: kpp || null,
    smevRequestStatus: smevRequestStatus || null,
    rafpNza: rafpNza || null,
    isForeignSubjectAgencyNotRegistredInRf: null,
    country: country || null,
    isSonko: typeof isSonko === 'boolean' ? isSonko : null,
    locationAddress: locationFields.address,
    workAddress: workAddress || null,
    factualAddress: factualAddress || null,
    snils: snils || null,
    document: document || null,
    epguNotifications: null,
    ervkId: ervkId || null,
    isPresentInItRegistry: isPresentInItRegistry ?? null,
    itAccreditationResponse: itAccreditationResponse ?? null,
    smevItAccreditationRequestStatus: smevItAccreditationRequestStatus ?? null,
    position: position ?? null,
    inspectionResults,
    isFilledFromLink: isFilledFromLink || null,
    locationRegion: locationFields.region,
    locationNotInFIAS: typeof locationFields.notInFIAS === 'boolean' ? locationFields.notInFIAS : null,
    locationManualAddress: locationFields.manualAddress,
    locationFiasRegion: locationFields.fiasRegion,
    locationRegionCodeFromFns: locationFields.locationRegionCodeFromFns || null,
  };
};
